import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDataProvider, useGetIdentity, useNotify } from 'react-admin';

import ExceptionClientList from './ExceptionClientList';
import InvoiceExceptionsSection from './InvoiceExceptionSection';
import PaExceptionsSection from './PaExceptionSection';
import AssistExceptionsSection from './AssistExceptionSection';
import { ClientDataType, ExceptionExtractParams, ExceptionReportName, ReportExceptionConfig } from '../utils/DataTypes';
import { DEFAULTS } from '../constants';
import { DEFAULT_EXCEPTION_CONFIG } from './exceptionDefaults';

const ExceptionConfigPage = () => {
  const dataProvider = useDataProvider();

  const [selectedClient, setSelectedClient] = useState<ClientDataType | null>(null);

  const [loading, setLoading] = useState(false);

  const [configs, setConfigs] = useState<ReportExceptionConfig[]>([]);

  const [invoiceState, setInvoiceState] = useState<ExceptionExtractParams>({ fields: [] });
  const [paState, setPaState] = useState<ExceptionExtractParams>({ fields: [] });
  const [assistState, setAssistState] = useState<ExceptionExtractParams>({ fields: [] });

  const [invoiceBase, setInvoiceBase] = useState<ExceptionExtractParams>({ fields: [] });
  const [paBase, setPaBase] = useState<ExceptionExtractParams>({ fields: [] });
  const [assistBase, setAssistBase] = useState<ExceptionExtractParams>({ fields: [] });

  const [showExceptionClients, setShowExceptionClients] = useState(false);

  const { identity } = useGetIdentity();

  const notify = useNotify();

  const clientNumber = selectedClient?.client_number ?? selectedClient?.id;

  const isDirty = (current: ExceptionExtractParams, base: ExceptionExtractParams) =>
    JSON.stringify([...current.fields].sort()) !== JSON.stringify([...base.fields].sort());

  const hasBackendConfig = (report: ExceptionReportName) => configs.some((c) => c.report_name === report);

  const handleToggleExceptionClients = (checked: boolean) => {
    setShowExceptionClients(checked);
    setSelectedClient(null);
  };

  const saveConfig = async (
    reportName: ExceptionReportName,
    extractParams: ExceptionExtractParams,
    setBase: (v: ExceptionExtractParams) => void
  ) => {
    if (!clientNumber) return;

    setLoading(true);
    try {
      await dataProvider.create('ReportExceptions', {
        data: {
          client_number: clientNumber,
          report_name: reportName,
          extract_parms: extractParams,
          created_by: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      });

      setBase(extractParams);
      notify('notifications.REPORT_EXCEPTION.SUCCESS', {
        type: 'success',
        messageArgs: { reportName: reportName },
      });
    } catch (error) {
      notify('notifications.REPORT_EXCEPTION.FAILED', {
        type: 'error',
        messageArgs: { reportName: reportName },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientNumber) return;

    let isMounted = true;

    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const response = await dataProvider.getOne('ReportExceptions', {
          id: clientNumber,
        });

        if (!isMounted) return;

        const fetchedConfigs: ReportExceptionConfig[] = response?.data?.configs ?? [];

        setConfigs(fetchedConfigs);

        const invoice = fetchedConfigs.find((c) => c.report_name === 'Invoice');
        const pa = fetchedConfigs.find((c) => c.report_name === 'PA');
        const assist = fetchedConfigs.find((c) => c.report_name === 'Assist');

        const invoiceExtract = invoice?.extract_parms?.fields?.length
          ? invoice.extract_parms
          : DEFAULT_EXCEPTION_CONFIG.Invoice;

        const invoiceLayout = invoice?.extract_parms?.layout ?? 'default';

        const paExtract = pa?.extract_parms?.fields?.length ? pa.extract_parms : DEFAULT_EXCEPTION_CONFIG.PA;

        const assistExtract = assist?.extract_parms?.fields?.length
          ? assist.extract_parms
          : DEFAULT_EXCEPTION_CONFIG.Assist;

        setInvoiceState(invoiceExtract);
        setPaState(paExtract);
        setAssistState(assistExtract);

        setInvoiceBase(invoiceExtract);
        setPaBase(paExtract);
        setAssistBase(assistExtract);
      } catch (e) {
        if (isMounted) {
          setConfigs([]);
          setInvoiceState({ fields: [] });
          setPaState({ fields: [] });
          setAssistState({ fields: [] });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchConfigs();

    return () => {
      isMounted = false;
    };
  }, [clientNumber, dataProvider]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          mb: 3,
          maxWidth: 700,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <ExceptionClientList
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            label="Client to Configure"
            reportType="Clients"
            showExceptionClients={showExceptionClients}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showExceptionClients}
              onChange={(e) => setShowExceptionClients(e.target.checked)}
            />
          }
          label="Only clients with exception config"
          sx={{ whiteSpace: 'nowrap' }}
        />
      </Box>

      {selectedClient && (
        <>
          <InvoiceExceptionsSection
            formState={invoiceState}
            setFormState={setInvoiceState}
            selectedLayout={invoiceState.layout ?? 'default'}
            isLoading={loading}
            isDefault={!hasBackendConfig('Invoice')}
            canSave={isDirty(invoiceState, invoiceBase)}
            onReset={() => setInvoiceState(DEFAULT_EXCEPTION_CONFIG.Invoice)}
            onSave={() => saveConfig('Invoice', invoiceState, setInvoiceBase)}
          />
          <PaExceptionsSection
            formState={paState}
            setFormState={setPaState}
            isLoading={loading}
            isDefault={!hasBackendConfig('PA')}
            canSave={isDirty(paState, paBase)}
            onReset={() => setPaState(DEFAULT_EXCEPTION_CONFIG.PA)}
            onSave={() => saveConfig('PA', paState, setPaBase)}
          />

          <AssistExceptionsSection
            formState={assistState}
            setFormState={setAssistState}
            isLoading={loading}
            isDefault={!hasBackendConfig('Assist')}
            canSave={isDirty(assistState, assistBase)}
            onReset={() => setAssistState(DEFAULT_EXCEPTION_CONFIG.Assist)}
            onSave={() => saveConfig('Assist', assistState, setAssistBase)}
          />
        </>
      )}
    </Box>
  );
};

export default ExceptionConfigPage;
